import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import firebase from 'firebase/app';
import 'firebase/auth';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LocalStoreService } from 'src/app/shared/services/local-store.service';
import jwt_decode from 'jwt-decode';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { clearUserDetailsData } from 'src/app/store/user-details/user-details.actions';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  supportedPopupSignInMethods = [
    firebase.auth.GoogleAuthProvider.PROVIDER_ID,
    firebase.auth.FacebookAuthProvider.PROVIDER_ID,
    firebase.auth.GithubAuthProvider.PROVIDER_ID,
  ];

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private firebaseAuth: AngularFireAuth,
    private translateService: TranslateService,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: string,
    private localStoreService: LocalStoreService,
    private router: Router
  ) {
    this.authStatusListener();
  }

  currentUser = null;
  currentCountry$: any = new BehaviorSubject(null);
  currentCurrency$: any = new BehaviorSubject(null);

  public authStatusSub = new BehaviorSubject(this.currentUser);
  currentAuthStatus = this.authStatusSub.asObservable();

  public _subject = new Subject<object>();
  public event = this._subject.asObservable();

  public _userAccountRefreshSubject = new Subject<void>();
  public userAccountRefresh$ = this._userAccountRefreshSubject.asObservable();

  public publishUserName(data: any) {
    this._subject.next(data);
  }

  public refreshUserAccount() {
    this._userAccountRefreshSubject.next();
  }

  authStatusListener() {
    this.firebaseAuth.authState.subscribe((credential) => {
      if (credential) {
        this.authStatusSub.next(credential);
      } else {
        this.authStatusSub.next(null);
        // The store holds ONE account, the one signed in: once Firebase reports
        // no credential, nothing may stay in it. logOut clears it too, but it
        // does not wait for signOut(), so for a few ticks the credential still
        // names the old account and a late getUserAccount answer for it passes
        // the ownership gate and lands AFTER that clear. Clearing again here,
        // on the fact (credential null) rather than on the intent, wipes it.
        this.store.dispatch(clearUserDetailsData());
      }
    });
  }

  get authStatus() {
    return this.authStatusSub;
  }

  getDefaultCountry(): Observable<any> {
    const url = environment.apiUrl + `/country/getDefaultCountry`;
    const response: any = this.http
      .get<any>(url, this.httpOptions)
      .subscribe((res) => {
        this.currentCountry$.next(res);
        this.localStoreService.getItem('user')
          ? this.currentCurrency$.next(res?.currency)
          : this.currentCurrency$.next('Cr');
      });
    return response;
  }

  getUserCountry(): Observable<any> {
    const url = environment.apiUrl + `/user/getUserAccount`;
    const response: any = this.http
      .get<any>(url, this.httpOptions)
      .subscribe((res) => {
        if (res?.country) {
          this.currentCountry$.next(res.country);
          this.currentCurrency$.next(res.country.currency);
        } else {
          this.getDefaultCountry();
          return;
        }
      });
    return response;
  }

  loginWithMail = async (formValue: { email: string; password: string }) => {
    try {
      firebase.database().goOnline();
      const user = await this.firebaseAuth.signInWithEmailAndPassword(
        formValue.email,
        formValue.password
      );
      if (user.user.emailVerified) {
        this.localStoreService.setItem('user', user);
        this.publishUserName(user.user.displayName);
        const userIdToken = await user.user.getIdTokenResult();
        const userRole = userIdToken['claims']['adminRole'];
        this.getUserCountry();
        return {
          success: true,
          message: 'ALERT.WELCOME',
          data: userRole,
        };
      } else {
        return {
          success: false,
          message: 'ALERT.VERIFY_YOUR_EMAIL',
        };
      }
    } catch (err) {
      console.log('Errr Login', err);
      return {
        success: false,
        message: err.code.includes('auth/too-many-requests')
          ? this.translateService.instant('ALERT.OUT_OF_ORDER')
          : this.translateService.instant(
              'ALERT.USERNAME_OR_PASSWORD_IS_INCORRECT'
            ),
      };
    }
  };

  resetPasswordWithUsername = async (email) => {
    try {
      await this.firebaseAuth.sendPasswordResetEmail(email);
      return {
        success: true,
        message:
          'The reset link has been successfully sent to your mail, please go check your inbox.',
      };
    } catch (err) {
      return {
        success: false,
        message: this.translateService.instant('ALERT.USERNAME_NOTFOUND'),
      };
    }
  };

  resetPassword = async (code, formValue: { password: string }) => {
    try {
      await this.firebaseAuth.confirmPasswordReset(code, formValue.password);
      return {
        success: true,
        message: 'ALERT.PASSWORD_SUCCESSFULLY_RESET',
      };
    } catch (err) {
      return {
        success: false,
        message: 'ALERT.SOMETHING_WENT_WRONG',
      };
    }
  };

  verifyEmail = async (verifyCode) => {
    try {
      await this.firebaseAuth.applyActionCode(verifyCode);
      return {
        success: true,
        message: 'ALERT.EMAIL_SUCCESSFULLY_VERIFIED',
      };
    } catch (err) {
      return {
        success: false,
        message: 'ALERT.SOMETHING_WENT_WRONG',
      };
    }
  };

  // Server-side half of logout. signOut() only clears this browser's own
  // copy — the refresh token it held stays valid at Firebase and can be
  // exchanged for fresh ID tokens indefinitely (pentest AUTH-VULN-08, proven
  // 60s after a logout). POST /user/logout revokes it for real.
  //
  // This MUST run before signOut(): the interceptor reads the token from
  // auth.idToken, which is already null once we have signed out, so the
  // request would go out anonymous and revoke nothing.
  //
  // Best effort, with a ceiling: a slow or failing server must never leave
  // someone stuck in a signed-in UI. If the call does not get through, the
  // token still expires on its own within the hour.
  private async revokeServerSession(): Promise<void> {
    try {
      const url = environment.apiUrl + `/user/logout`;
      await this.http
        .post(url, {}, this.httpOptions)
        .pipe(timeout(3000))
        .toPromise();
    } catch (err) {
      // deliberately ignored — see above
    }
  }

  logOut = async () => {
    try {
      await this.revokeServerSession();
      // firebase.database().goOffline();
      this.firebaseAuth.signOut();
      // Forget the account the moment its session is gone. The store used to
      // outlive a logout, so the previous account's role, balance and
      // per-account virtuals verdict stayed readable until some later
      // getUserAccount happened to overwrite them — and a different account
      // signing in on the same tab started from them. The verdict consumers
      // also require details.fbId === credential.uid; this makes the store
      // honest on its own.
      this.store.dispatch(clearUserDetailsData());
      this.localStoreService.removeItem('user');
      this.localStoreService.removeItem('supabaseToken');
      this.localStoreService.removeItem('supabaseTokenCreatedAt');
      this.getDefaultCountry();
      this.router.navigateByUrl('/');
      return {
        success: true,
      };
    } catch (err) {
      return {
        success: false,
      };
    }
  };

  async checkTokenExpiration() {
    try {
      const user:any = await this.localStoreService.getItem('user');
      if (user) {
        const decoded = await jwt_decode(user?.user.stsTokenManager.accessToken);
        if (decoded['exp'] === undefined) {
          this.logOut();
        }
        const date = new Date(0);
        date.setUTCSeconds(decoded['exp']);
        if (date === undefined || (!(date.valueOf() > Date.now()) && date)) {
          this.handleIdToken();
        }
        return true;
      }
      return false;
    } catch (error) {
      this.logOut();
    }
  }

  handleIdToken() {
    this.firebaseAuth.onIdTokenChanged(async (user) => {
      if (user) {
        let idToken = await user.getIdToken(true);
        let userLocal: any = await this.localStoreService.getItem('user');
        userLocal.user.stsTokenManager.accessToken = await idToken;
        this.localStoreService.setItem('user', userLocal);
      } else {
        this.logOut();
      }
    });
  }

  isTokenValid(token: string): boolean {
    const decoded = jwt_decode(token);
    if (decoded['exp'] === undefined) {
      return false;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded['exp']);
    if (date === undefined || (!(date.valueOf() > Date.now()) && date)) {
      return false;
    }
    return true;
  }

  getCurrentUser = () => {
    return this.firebaseAuth.authState.pipe((user) => {
      return user;
    });
  };

  sendEmailVerificationAndLogOut(customToken) {
    this.firebaseAuth
      .signInWithCustomToken(customToken)
      .then(async (UserCredential) => {
        await UserCredential.user.sendEmailVerification();
        // this.logOut();
      });
  }

  updatePassword = async (newPassword) => {
    try {
      const user = await this.firebaseAuth.currentUser;
      user.updatePassword(newPassword);
      return {
        success: true,
        message: 'ALERT.USER_PASSWORD_UPDATED',
      };
    } catch (err) {
      return {
        success: false,
        message: 'ALERT.SOMETHING_WENT_WRONG',
      };
    }
  };

  verifyCurrentPassword = async (password) => {
    const currentUser = await this.firebaseAuth.currentUser;

    const credential = firebase.auth.EmailAuthProvider.credential(
      currentUser.email,
      password
    );

    const status = await currentUser
      .reauthenticateWithCredential(credential)
      .then(() => true)
      .catch((err) => false);

    return status;
  };

  getCountriesPartial(page: number = 1, pageSize: number = 1000): Promise<any> {
    const url = environment.apiUrl + `/country/getCountriesPartial?page=${page}&pageSize=${pageSize}`;
    return this.http.get<any>(url, this.httpOptions).toPromise();
  }

 async oneClickRegistration(
    captchaResponse: string,
    countryId: number,
    deviceId?: string | null
  ) {
    const url = environment.apiUrl + `/user/one-click-registration`;

    this.httpOptions.headers = this.httpOptions.headers.set('x-recaptcha-response', captchaResponse);

    const body: { countryId: number; deviceId?: string } = { countryId };
    if (deviceId) {
      body.deviceId = deviceId;
    }

    const response = await this.http
      .post<any>(url, body, this.httpOptions)
      .toPromise();
    return response;
  }

  // Standard form registration — same reCAPTCHA header contract as one-click.
  // payload: { username, password, firstname, lastname, email?, phoneNumber,
  //            dateOfBirth (ISO), countryId, deviceId? }
  async registration(payload: any, captchaResponse: string) {
    const url = environment.apiUrl + `/user/registration`;

    this.httpOptions.headers = this.httpOptions.headers.set('x-recaptcha-response', captchaResponse);

    const response = await this.http
      .post<any>(url, payload, this.httpOptions)
      .toPromise();
    return response;
  }
}
